import { StatBlock } from "@/app/Dashboard/Components/Blocks/types";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ComparisonMode } from "@/gql/graphql";
import { useSuspenseGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { adminDashboardStatsQuery } from "@/app/Dashboard/GraphQL/adminDashboardStatsQuery";
import { toDateTimeTzString } from "@/lib/Formatters/toDateTimeString";
import colors from "tailwindcss/colors";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
} from "@/components/ui/chart";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  XAxis,
  YAxis,
} from "recharts";
import dayjs from "dayjs";
import { useTranslations } from "use-intl";
import { TrendIndicator } from "@/app/Dashboard/Components/Blocks/TrendIndicator";

export function DailyBookingStatBlock({
  end,
  comparisonMode,
  start,
}: StatBlock) {
  const t = useTranslations();
  const { data: adminData } = useSuspenseGqlQuery(adminDashboardStatsQuery, {
    start: toDateTimeTzString(start),
    end: toDateTimeTzString(end),
    comparisonMode,
  });

  const total = adminData.adminDashboardStats.bookings.reduce(
    (sum, day) =>
      sum +
      day.count_cancelled +
      day.count_pending +
      day.count_confirmed +
      day.count_completed +
      day.count_vip,
    0,
  );

  return (
    <Card className="flex flex-col">
      <CardHeader className="space-y-0">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex-1">
            <CardTitle className="text-base font-medium">
              {t("dashboard.blocks.bookingTrends.title")}
            </CardTitle>
          </div>
          <div className="flex shrink-0 flex-col items-start gap-2 sm:items-end">
            <CardDescription className="text-foreground text-2xl font-bold">
              {total.toLocaleString()}
            </CardDescription>
            <TrendIndicator
              trend={adminData.adminDashboardStats.bookings_total_trend}
              currentValue={total}
              startDate={start}
              endDate={end}
              comparisonMode={comparisonMode}
              formatValue={(value) => Math.round(value).toLocaleString()}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="mt-auto min-w-0 pt-6">
        <BookingAreaChart
          start={start}
          end={end}
          comparisonMode={comparisonMode}
        />
      </CardContent>
    </Card>
  );
}

function BookingAreaChart({
  start,
  end,
  comparisonMode,
}: {
  start: string;
  end: string;
  comparisonMode: ComparisonMode;
}) {
  const t = useTranslations();
  const { data } = useSuspenseGqlQuery(adminDashboardStatsQuery, {
    start: toDateTimeTzString(start),
    end: toDateTimeTzString(end),
    comparisonMode,
  });

  const startDate = dayjs(start);
  const endDate = dayjs(end);
  const isSingleDay = startDate.isSame(endDate, "day");
  const daysBetween = endDate.diff(startDate, "day");

  const chartData = isSingleDay
    ? Array.from({ length: 24 }, (_, hour) => {
        const hourStr = startDate.hour(hour).format("YYYY-MM-DD HH:00:00");
        const hourData = data.adminDashboardStats.bookings.find(
          (item) => item.date === hourStr,
        );

        return {
          date: hourStr,
          count_cancelled: hourData?.count_cancelled || 0,
          count_pending: hourData?.count_pending || 0,
          count_confirmed: hourData?.count_confirmed || 0,
          count_completed: hourData?.count_completed || 0,
          count_vip: hourData?.count_vip || 0,
        };
      })
    : Array.from({ length: daysBetween + 1 }, (_, index) => {
        const date = startDate.add(index, "day").format("YYYY-MM-DD");
        const dayData = data.adminDashboardStats.bookings.find(
          (day) => day.date === date,
        );

        return {
          date,
          count_cancelled: dayData?.count_cancelled || 0,
          count_pending: dayData?.count_pending || 0,
          count_confirmed: dayData?.count_confirmed || 0,
          count_completed: dayData?.count_completed || 0,
          count_vip: dayData?.count_vip || 0,
        };
      });

  const chartConfig = {
    count_cancelled: {
      label: t("dashboard.chartLabels.cancelled"),
      color: colors["red"]["500"],
    },
    count_vip: {
      label: t("dashboard.chartLabels.vip"),
      color: colors["purple"]["500"],
    },
    count_pending: {
      label: t("dashboard.chartLabels.pending"),
      color: colors["yellow"]["500"],
    },
    count_confirmed: {
      label: t("dashboard.chartLabels.confirmed"),
      color: colors["green"]["500"],
    },
    count_completed: {
      label: t("dashboard.chartLabels.completed"),
      color: colors["blue"]["500"],
    },
  } satisfies ChartConfig;

  return (
    <ChartContainer config={chartConfig}>
      <AreaChart
        data={chartData}
        margin={{
          left: -20,
          right: 12,
        }}
      >
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="date"
          tickLine={false}
          axisLine={false}
          tickMargin={10}
          tickFormatter={(value) =>
            isSingleDay
              ? dayjs(value).format("HH:mm")
              : dayjs(value).format("DD MMM")
          }
        />
        <YAxis
          axisLine={false}
          tickSize={10}
          type="number"
          domain={[
            0,
            (dataMax: number) =>
              Math.max(Math.ceil((500 / 7 + 1) / 10) * 10, dataMax),
          ]}
        />
        <ReferenceLine
          y={500 / 7}
          label={t("dashboard.blocks.bookingTrends.targetLabel")}
          stroke="hsl(var(--chart-2))"
        />
        <ChartTooltip
          content={(props) => {
            if (!props.active || !props.payload?.length) return null;
            const total = props.payload.reduce(
              (sum, item) => sum + (item.value || 0),
              0,
            );
            return (
              <div className="border-border/50 bg-background grid min-w-32 items-start gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs shadow-xl">
                <div className="font-medium">
                  {isSingleDay
                    ? dayjs(props.label).format("ddd, HH:mm")
                    : dayjs(props.label).format("ddd, DD MMM")}
                </div>
                <div className="grid gap-1.5">
                  {props.payload.map((item) => (
                    <div
                      key={item.dataKey}
                      className="flex items-center justify-between gap-4"
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className="h-2.5 w-2.5 shrink-0 rounded-[2px]"
                          style={{ backgroundColor: item.color }}
                        />
                        <span className="text-muted-foreground">
                          {chartConfig[item.dataKey as keyof typeof chartConfig]
                            ?.label || item.name}
                        </span>
                      </div>
                      <span className="text-foreground font-mono font-medium tabular-nums">
                        {item.value?.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="border-border mt-1 flex items-center justify-between gap-4 border-t pt-1.5 font-medium">
                  <span className="text-muted-foreground">
                    {t("common.total")}
                  </span>
                  <span className="text-foreground font-mono tabular-nums">
                    {total.toLocaleString()}
                  </span>
                </div>
              </div>
            );
          }}
        />
        <Area
          dataKey="count_completed"
          type="basis"
          fill="var(--color-count_completed)"
          fillOpacity={0.4}
          stroke="var(--color-count_completed)"
          stackId="a"
        />
        <Area
          dataKey="count_confirmed"
          type="basis"
          fill="var(--color-count_confirmed)"
          fillOpacity={0.4}
          stroke="var(--color-count_confirmed)"
          stackId="a"
        />
        <Area
          dataKey="count_pending"
          type="basis"
          fill="var(--color-count_pending)"
          fillOpacity={0.4}
          stroke="var(--color-count_pending)"
          stackId="a"
        />
        <Area
          dataKey="count_vip"
          type="basis"
          fill="var(--color-count_vip)"
          fillOpacity={0.4}
          stroke="var(--color-count_vip)"
          stackId="a"
        />
        <Area
          dataKey="count_cancelled"
          type="basis"
          fill="var(--color-count_cancelled)"
          fillOpacity={0.4}
          stroke="var(--color-count_cancelled)"
          stackId="a"
        />
      </AreaChart>
    </ChartContainer>
  );
}
